/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  ValidationErrors,
  Validator
} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {takeUntil} from 'rxjs/operators';

import * as moment from 'moment';

import {DatePickerInnerComponent} from '../legacy-datepicker';
import {DATE_FORMAT, DATE_MAX, DATE_MIN, getMonthFormat, GP_MONTH_MASK} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {GroupZoneDescriptor} from '../../form-generator/form-generator.interfaces';
import {LocalizationService} from '../../localization/localization.service';
import {differenceInDays, isValid} from '../../utils/date';
import {isPresent} from '../../utils/lang';

const DATEPICKER_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatePickerInputComponent),
  multi: true
};
const DATEPICKER_VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => DatePickerInputComponent),
  multi: true
};

/**
 * Datepicker input component that opens up a datepicker when clicking on the input
 */
@Component({
  selector: 'datepicker-input, datepicker-input[ngModel]',
  template: `
    <div class="form-group" #mainElement>
      <div class="input-group">
        <input #input
               type="text"
               class="form-control date-input"
               [class.gp-error]="hasErrors()"
               (focus)="onFocus()"
               (blur)="onBlur()"
               [readOnly]="readOnly"
               [ngModel]="activeDate"
               [formControl]="dateControl"
               [placeholder]="placeholder"
               (keydown.enter)="$event.preventDefault()"
               [textMask]="dateMask"/>
        <div #calendar class="input-group-addon pointer" *ngIf="!readOnly" (click)="openCalendar()">
          <i aria-hidden="true" class="fa fa-light fa-calendar-alt"></i>
        </div>
      </div>
      <div #popup class="datepicker-wrapper" [attr.data-hidden]="!open">
        <datepicker-inner [activeDate]="activeDateAsDate"
                          (update)="onUpdate($event)"
                          [initDate]="initDate"
                          [minDate]="minDate"
                          [maxDate]="maxDate"
                          [datepickerMode]="datepickerMode"
                          [startingDay]="startingDay"
                          [yearRange]="yearRange"
                          [minMode]="minMode"
                          [maxMode]="maxMode"
                          [showWeeks]="showWeeks"
                          [formatDay]="formatDay"
                          [formatMonth]="formatMonth"
                          [formatYear]="formatYear"
                          [formatDayHeader]="formatDayHeader"
                          [formatDayTitle]="formatDayTitle"
                          [formatMonthTitle]="formatMonthTitle"
                          [onlyCurrentMonth]="onlyCurrentMonth"
                          [monthColLimit]="monthColLimit"
                          [yearColLimit]="yearColLimit"
                          [shortcutPropagation]="shortcutPropagation"
                          [customClass]="customClass"
                          [dateDisabled]="dateDisabled"
                          #datePickerInner>
          <daypicker tabindex="0" class="daypicker"></daypicker>
          <monthpicker tabindex="0" class="monthpicker"></monthpicker>
          <yearpicker tabindex="0" class="yearpicker"></yearpicker>
          <div class="datepicker-bottom-bar">
            <button *ngIf="hasEternity" type="button" class="btn btn-default btn-secondary btn-sm pull-left"
                    (click)="setEternity('minus')">- ∞
            </button>
            <button test-id="todayButton"
                    type="button"
                    class="btn btn-default btn-secondary btn-sm"
                    (click)="setToday($event)">
              <strong>{{ 'REPORTS.today' | translate }}</strong>
            </button>
            <button test-id="clearButton"
                    type="button"
                    class="btn btn-default btn-secondary btn-sm"
                    (click)="clear($event)">
              {{ 'REPORTS.clear' | translate }}
            </button>
            <button *ngIf="hasEternity" type="button" class="btn btn-default btn-secondary btn-sm pull-right"
                    (click)="setEternity('plus')">+ ∞
            </button>
          </div>
        </datepicker-inner>
      </div>
      <ul class="error-list" [attr.data-hidden]="!hasErrors() && dateControl.valid">
        <li>{{ 'EMPLOYEE_HIRE.not_a_valid_date' | translate }}</li>
      </ul>
      <ul class="error-list" *ngIf="errors?.minDateError">
        <li>{{ minDateErrorMessage | translate }}</li>
      </ul>
      <ul class="error-list" *ngIf="errors?.maxDateError">
        <li>{{ maxDateErrorMessage | translate }}</li>
      </ul>
    </div>
  `,
  providers: [DATEPICKER_VALUE_ACCESSOR, DATEPICKER_VALIDATOR],
  styleUrls: ['./datepicker-input.component.scss']
})
export class DatePickerInputComponent extends Destroyable implements ControlValueAccessor, OnInit, Validator {

  @Input() public initDate: Date;
  @Input() public minDate: Date = void 0;
  @Input() public maxDate: Date = void 0;
  @Input() public datepickerMode: string = 'day';
  @Input() public startingDay: number = 1;
  @Input() public yearRange: number = 20;
  @Input() public minMode: string = 'day';
  @Input() public maxMode: string = 'year';
  @Input() public showWeeks = false;
  @Input() public formatDay: string = 'DD';
  @Input() public formatMonth: string = 'MMMM';
  @Input() public formatYear: string = 'YYYY';
  @Input() public formatDayHeader: string = 'dd';
  @Input() public formatDayTitle: string = 'MMMM YYYY';
  @Input() public formatMonthTitle: string = 'YYYY';
  @Input() public onlyCurrentMonth: boolean = false;
  @Input() public monthColLimit: number = 3;
  @Input() public yearColLimit: number = 5;
  @Input() public shortcutPropagation: boolean = false;
  @Input() public customClass: Array<{ date: Date, mode: string, clazz: string }>;
  @Input() public dateDisabled: any;

  @Input() public hasEternity: boolean = false;
  @Input() public groupZoneID: string = '';
  @Input() public groupZoneDescriptor: GroupZoneDescriptor;
  @Input() public correctToExtremeValues: boolean = true;
  @Input() public dateRangeCorrection: boolean = false;
  @Input() public isAppendToBody: boolean = false;

  @Input() public maxDateMoment: any = DATE_MAX;
  @Input() public minDateMoment: any = DATE_MIN;

  public onChange: any = Function.prototype;
  public onTouched: any = Function.prototype;

  public dateMask: any;
  public dateFormat: string;

  public isValid: boolean = true;
  public isEmpty: boolean = true;

  public open: boolean = false;
  public openLeft: boolean = false;
  public visible: boolean;
  public dateControl: UntypedFormControl = new UntypedFormControl();

  @ViewChild('datePickerInner', {static: true})
  public _datePicker: DatePickerInnerComponent;

  @ViewChild('calendar', {static: false})
  public calendar: ElementRef;

  @ViewChild('input', {static: true})
  public inputEl: ElementRef;

  @Input()
  public readOnly: boolean = false;

  @ViewChild('popup', {static: true})
  public popup: ElementRef;

  @ViewChild('mainElement', {static: true})
  public mainElement: ElementRef;

  @Input() public parentScrollSelector: string;

  public isFocused: boolean = false;
  public errors: ValidationErrors | null = null;

  @Input() public displayFormat: string;
  @Input() public minDateErrorMessage = 'FORMS.VALIDATORS.contract_end_date_earlier_start_date';
  @Input() public maxDateErrorMessage = '';

  @Output()
  public focus: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public blur: EventEmitter<void> = new EventEmitter<void>();

  private _activeDate: string;
  private _activeDateAsDate: Date;
  private _lastMode: string;
  private today: Date = moment().startOf('day').toDate();


  constructor(private element: ElementRef,
              private changeDetectorRef: ChangeDetectorRef,
              private localization: LocalizationService,
              private translate: TranslateService) {
    super();
    this.dateFormat = localization.dateFormat;
    this.dateMask = localization.dateMask;
  }

  @HostListener('document:click', ['$event', '$event.target'])
  public onClick(event: any, targetElement: HTMLElement): void {
    if (targetElement
      && !this.element.nativeElement.contains(targetElement)
      && !this.popup.nativeElement.contains(targetElement)) {
      this.clickOutside();
    }
  }

  public ngOnInit() {
    this.handleFormatForMonthMode();
    Object.defineProperty(this._datePicker, 'dateFormatter', {
      value: {
        format: function (date, format) {
          return moment(date.getTime()).format(format);
        }
      }
    });

    this.dateControl.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(value => {
        if (value === '' || isValid(value, this.dateFormat)) {
          this.hasTyped(value);
          this.dateControl.setErrors(null);
        } else {
          this.dateControl.setErrors({'invalidDate': true});
        }
      });

    this.localization.changeDateFormatEvent
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        const value = this._activeDate;
        this._activeDate = '';
        this.dateFormat = this.localization.dateFormat;
        setTimeout(() => {
          this.dateMask = this.localization.dateMask;
          this.handleFormatForMonthMode();
          this._activeDate = value;
          this.onChange(value);
        });
      });

    this.translate.onLangChange
      .asObservable()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this._datePicker.refreshView());
  }

  private handleFormatForMonthMode() {
    if (this.datepickerMode === 'month') {
      this.dateFormat = getMonthFormat(this.dateFormat);
      this.dateMask = GP_MONTH_MASK[this.dateFormat];
    }
  }

  @Input()
  public get activeDate(): string {
    if (this.hasEternity) {
      const curDate = moment(this._activeDate);
      if (curDate.isSame(this.maxDateMoment) || curDate.isSame(this.minDateMoment)) {
        return '';
      }
    }

    return isValid(this._activeDate, DATE_FORMAT)
      ? moment(this._activeDate).format(this.dateFormat || this.displayFormat)
      : this._activeDate;
  }

  public set activeDate(value: string) {
    if (value !== this._activeDate && this.isDatePickerSelectable()) {
      this._activeDate = value;

      if (isValid(this._activeDate)) {
        this.onChange(value);
        this._activeDateAsDate = moment(value).toDate();
      } else {
        this._activeDateAsDate = undefined;
      }
    }
  }

  public get activeDateAsDate(): Date {
    return this._activeDateAsDate || this.today;
  }

  public get placeholder(): string {
    const curDate = moment(this._activeDate);
    let placeholder: string = this.dateFormat || '';

    if (this.hasEternity) {
      if (curDate.isSame(this.maxDateMoment)) {
        placeholder = '+ ∞';
      } else if (curDate.isSame(this.minDateMoment)) {
        placeholder = '- ∞';
      }
    }
    return placeholder;
  }

  public openCalendar(): void {
    this.open = !this.open;

    if (this.open) {
      this.setPosition();
      if (this.activeDate === '') {
        this._activeDateAsDate = this.today;
      }
    }
    this.hasTyped(this.activeDate.toString());

    this.changeDetectorRef.detectChanges();
  }

  public setEternity(sign: string): void {
    switch (sign) {
      case 'plus':
        this.activeDate = isValid(this.maxDate)
          ? moment(this.maxDate).format(DATE_FORMAT)
          : this.maxDateMoment;
        break;
      case 'minus':
        this.activeDate = isValid(this.minDate)
          ? moment(this.minDate).format(DATE_FORMAT)
          : this.minDateMoment;
        break;
      default:
        break;
    }
    this.isValid = true;
    this.open = false;
  }

  public onUpdate(activeDate: Date): void {
    this.isValid = true;

    if (this.activeDate === '' || this._activeDateAsDate == null
      || differenceInDays(activeDate, this._activeDateAsDate) !== 0) {
      this.writeValue(moment(activeDate).format(DATE_FORMAT));
    }
  }

  public writeValue(value: any): void {
    this.activeDate = isValid(value) ? value : '';
  }

  public registerOnChange(fn: (_: any) => {}): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  public validate(c: AbstractControl): ValidationErrors | null {
    setTimeout(() => {
      this.errors = c.errors;
      this.changeDetectorRef.markForCheck();
    });

    return;
  }

  public onFocus() {
    this.isFocused = true;
    if (this.groupZoneDescriptor) {
      this.groupZoneDescriptor.isFocused = true;
    }
    this.focus.emit();
  }

  public onBlur() {
    this.isFocused = false;
    if (this.groupZoneDescriptor) {
      this.groupZoneDescriptor.isFocused = false;
      this.inputEl.nativeElement.value = this.activeDate;
    }
    this.blur.emit();
  }

  public setToday(event: any) {
    event.preventDefault();
    this.hasTyped(moment().format(this.dateFormat));
    this.open = false;
  }

  public clear(event: any) {
    event.preventDefault();
    this.hasTyped('');
    this.open = false;
  }

  public hasErrors() {
    return this.activeDate !== '' && !this.isEmpty && !isValid(this.activeDate, this.dateFormat || '');
  }

  private setPosition() {
    const popup: HTMLElement = this.popup.nativeElement;
    popup.style.marginTop = '';
    popup.style.marginLeft = '';
    popup.style.visibility = 'hidden';
    popup.removeAttribute('data-hidden');

    const dropdownRect = popup.getBoundingClientRect();
    const viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

    const isOutOfScreen = dropdownRect.right > viewportWidth;
    if (isOutOfScreen) {
      popup.style.marginLeft = `${viewportWidth - dropdownRect.right}px`;
    }

    if (dropdownRect.top + dropdownRect.height > viewportHeight) {
      // Flip the calendar above the field. Lift it by its own height PLUS the
      // input group's height (plus a small gap) so the popup fully clears the
      // input instead of overlapping it.
      const inputGroup: HTMLElement = this.inputEl.nativeElement.parentElement;
      const anchorHeight = (inputGroup || this.inputEl.nativeElement).offsetHeight || 0;
      popup.style.marginTop = `${-dropdownRect.height - anchorHeight - 8}px`;
    }

    if (this.parentScrollSelector) {
      const parent = this.popup.nativeElement.closest(this.parentScrollSelector);
      if (parent && isPresent(parent.scrollLeft)) {
        const offset: number = isOutOfScreen
          ? (dropdownRect.width - this.mainElement.nativeElement.getBoundingClientRect().width)
          : 0;
        popup.style.marginLeft = `-${parent.scrollLeft + offset}px`;
      }
    } else if (this.isAppendToBody) {
      const calendarRect: any = this.calendar.nativeElement.getBoundingClientRect();
      popup.style.left = `${calendarRect.left + window.scrollX}px`;
      popup.style.top = `${calendarRect.top + window.scrollY + this.calendar.nativeElement.clientHeight}px`;
      document.body.appendChild(this.popup.nativeElement);
    }

    popup.style.visibility = 'visible';
  }

  private hasTyped(value: string) {
    if (value === ''
      && (moment(this._activeDate).isSame(DATE_MAX)
        || moment(this._activeDate).isSame(DATE_MIN))) {
      this.activeDate = this._activeDate;
      return;
    }

    this.isEmpty = value === '';
    if (this.isEmpty) {
      this.onChange('');
    }

    value = value.trim();
    this.isValid = isValid(value, this.dateFormat || '');
    if (this.isValid && this._datePicker) {
      value = moment(value, this.dateFormat).format(DATE_FORMAT);
      const date: Date = moment(value).toDate();
      this._datePicker.select(date);
    } else {
      this.activeDate = value;
    }
  }

  private clickOutside() {
    const innerDatePickerSelectable = this.isInnerDatePickerSelectable();
    this._lastMode = this._datePicker.datepickerMode;

    if (innerDatePickerSelectable && this.isDatePickerSelectable()) {
      this.open = false;
    }
  }

  private isDatePickerSelectable(): boolean {
    return !this._lastMode || this.datepickerMode === this._lastMode;
  }

  private isInnerDatePickerSelectable(): boolean {
    return !this._lastMode || this._datePicker.datepickerMode === this._lastMode;
  }

}
